import { useEffect, useState } from 'react'
import { RootState } from 'store'
import { useAppDispatch } from 'store/hooks'
import { actions as companyActions } from 'store/companySearch'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Title from 'components/title/Title'

import styles from './CompanySearchPage.module.scss'
import Spinner from 'components/spinner/Spinner'
import { StyledInputWrapper } from 'containers/search/StyledInputWrapper'
import LockIcon from '@mui/icons-material/Lock'
import { Box, Button, CircularProgress, Link, TextField, Tooltip, Typography } from '@mui/material'
import { SuggestedCompany } from 'store/companySearch/types'

function CompanySearchPage() {
  const dispatch = useAppDispatch()
  const history = useNavigate()

  const suggestions = useSelector((state: RootState) => state.companySearch.suggestions)
  const searchResponse = useSelector((state: RootState) => state.companySearch?.searchResponse)
  const isLoading = useSelector((state: RootState) => state.companySearch.loading)

  const error = useSelector((state: RootState) => state.companySearch.error)
  const [companySearchIsSubmitted, setCompanySearchIsSubmitted] = useState<boolean>(false)
  const [companyName, setCompanyName] = useState<string>('')
  const [companyNameError, setCompanyNameError] = useState<boolean>(false)
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([])
  const [showMoreResults, setShowMoreResults] = useState<boolean>(false)

  useEffect(() => {
    //Clear states
    dispatch(companyActions.setError({ key: 'search', value: null }))
    dispatch(companyActions.setSuggestedCompanies({ suggestedCompanies: [] }))
  }, [dispatch])

  const handleInputChange = (value: string) => {
    if (value.length > 1) dispatch(companyActions.getSuggestedCompanies(value))
    else dispatch(companyActions.setSuggestedCompanies({ suggestedCompanies: [] }))

    const suggestionNames = suggestions.map(suggestion => suggestion.display_name)
    const validSelectedCompanies = selectedCompanies.filter(option => suggestionNames.includes(option))
    setSelectedCompanies(validSelectedCompanies)
  }

  const handleSubmit = () => {
    setCompanySearchIsSubmitted(true)
    dispatch(companyActions.submitCompanySearch(selectedCompanies))
  }

  const handleCheckboxChange = (option: string) => {
    if (selectedCompanies.includes(option)) {
      setSelectedCompanies(selectedCompanies.filter(item => item !== option))
    } else {
      setSelectedCompanies([...selectedCompanies, option])
    }
  }

  useEffect(() => {
    if (companySearchIsSubmitted && searchResponse) {
      history(`/company/search/${searchResponse?.ID}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResponse])

  const MultiSelect = ({ options }: { options: SuggestedCompany[] }) => {
    let withTicker = options
      .filter(option => option.tickers.length || option.aliases.length)
      .sort((a, b) => {
        //sort by displa_name length shorted first
        return a.display_name.length - b.display_name.length
      })

    let withoutTicker = options
      .filter(option => option.tickers.length === 0)
      .sort((a, b) => {
        //sort by displa_name length shorted first
        return a.display_name.length - b.display_name.length
      })

    return (
      <div className={styles.base}>
        <div className={styles.suggestedDropdown}>
          {withTicker.map(option => (
            <div className={styles.row}>
              <div className={styles.tickers}>{option.tickers.join(', ')}</div>

              <input
                type="checkbox"
                className={styles.checkbox}
                onChange={() => handleCheckboxChange(option.display_name)}
                checked={selectedCompanies.includes(option.display_name)}
              />
              {option.display_name.toLowerCase()}
              {option.aliases.length > 0 && <span> ({option.aliases.length})</span>}
            </div>
          ))}

          {withTicker.length > 0 && withoutTicker.length > 0 && (
            <>
              {!showMoreResults && (
                <Link
                  href=""
                  onClick={ev => {
                    ev.preventDefault()
                    setShowMoreResults(true)
                  }}>
                  <div className={styles.showMoreRow}>Show Unrefined Results</div>
                </Link>
              )}

              {showMoreResults && (
                <Link
                  href=""
                  onClick={ev => {
                    ev.preventDefault()
                    setShowMoreResults(false)
                  }}>
                  <div className={styles.showMoreRow}>Hide Unrefined Results</div>
                </Link>
              )}
            </>
          )}

          {(showMoreResults || (withTicker.length === 0 && withoutTicker.length > 0)) && (
            <>
              {withTicker.length > 0 && <hr />}

              {withoutTicker.map(option => (
                <div className={styles.row}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    onChange={() => handleCheckboxChange(option.display_name)}
                    checked={selectedCompanies.includes(option.display_name)}
                  />
                  {option.display_name.toLowerCase()}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    )
  }

  const Pill = ({ label, text }: { label: string; text?: string }) => {
    return (
      <Tooltip title={text}>
        <Box
          sx={{
            px: 2,
            paddingTop: '14px',
            paddingBottom: '14px',
            mr: 3,
            backgroundColor: 'lightGrey.main',
            borderRadius: '15px',
            fontSize: 15,
          }}>
          {label}
        </Box>
      </Tooltip>
    )
  }

  const SearchButton = () => {
    return (
      <Box display="flex" alignItems="center">
        <Button
          variant="contained"
          sx={{ px: 4, py: 2, height: '85px', fontSize: '30px', textTransform: 'none', width: '220px', borderRadius: '15px' }}
          disabled={isLoading.companySearch || selectedCompanies.length === 0}
          onClick={() => handleSubmit()}>
          Create
        </Button>
        {isLoading.companySearch && <CircularProgress size={18} color="inherit" />}
      </Box>
    )
  }

  return (
    <StyledInputWrapper>
      <Box
        sx={{
          display: 'block',
          textAlign: 'left',
          maxWidth: '887px',
          margin: '89px auto 0 auto',
          padding: '0 20px',
        }}>
        <Title variant="h4" sx={{ pb: '33px' }}>
          Create a report with a Company Name
        </Title>

        <Box display="inline-flex" flexDirection="row" mb={4}>
          <Pill
            label="Best for who?"
            text="Built for investors, analysts, and strategists. No patent knowledge or detailed company insight required"
          />
          <Pill
            label="How to search"
            text="Start typing a company name. Results refine as you type. Tick multiple checkboxes if the entries clearly relate to the same company. This combines their patent portfolios for analysis. Avoid mixing distinct companies unless you want a blended competitive view"
          />
          <Pill
            label="What happens?"
            text="We process your selection using advanced similarity algorithms across 160 million global patents. Behind the scenes, our system identifies, interprets, and compares key innovations. It builds technology sector fingerprints, maps cross-sector links, and finds companies most aligned by invention content."
          />
          <Pill
            label="What you get"
            text="The result is a Multi-Sector Report, grouped by highly specific patent sub-sectors. Each section reveals companies with similar technology, offering deep insight into direct and adjacent competitors"
          />
          <Pill
            label="How long will it take?"
            text="Submit your search and we will email you when it is ready. Depending on the complexity and depth of analysis, your report may take a few minutes or a few hours"
          />
        </Box>

        <Box
          sx={{
            display: 'block',
            padding: '0',
            marginTop: '44px',
          }}>
          <Box component="form" noValidate autoComplete="off">
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'left',
                gap: 2,
                mb: 1,
              }}>
              <TextField
                required
                id="company-name"
                name="companyName"
                label="Type a company name or ticker..."
                value={companyName}
                onChange={event => {
                  handleInputChange(event?.target.value)
                  setCompanyNameError(false)
                  setCompanyName(event?.target.value)
                }}
                slotProps={{
                  htmlInput: {
                    style: {
                      padding: '0 10px',
                      height: '85px',
                      lineHeight: '85px',
                      color: 'black',
                    },
                  },
                  inputLabel: {
                    style: {},
                  },
                }}
                sx={{
                  width: '100%',
                  minWidth: '280px',
                  maxWidth: { xs: '100%', sm: '547px' },
                  marginRight: '43px',
                  padding: '0',
                  lineHeight: '85px',
                  height: '85px',
                }}
              />
              <SearchButton />
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Your results are more precise as you enter more characters
              </Typography>

              <Link href="#" display="flex" sx={{ fontSize: '15px', marginRight: '30px' }}>
                Advanced search options
                <LockIcon fontSize="small" />
              </Link>
            </Box>

            <Box mb={2}>
              {error.search && (
                <Typography color="error" mt={1}>
                  {error.search}
                </Typography>
              )}
            </Box>

            {isLoading.suggestions && <Spinner />}
            {suggestions?.length > 0 && <MultiSelect options={suggestions} />}
            {companyNameError && <div className={styles.error}>Required</div>}
            {error.suggestions && <div className={styles.error}>{error.suggestions}</div>}
          </Box>
        </Box>
      </Box>
    </StyledInputWrapper>
  )
}

export default CompanySearchPage
